import { Head, Link, router } from "@inertiajs/react"
import { Suspense, lazy, useEffect, useMemo, useState } from "react"
import type { RefObject } from "react"

import { EditorShell } from "@/components/editor/EditorShell"
import { ScaleControls } from "@/components/editor/ScaleControls"
import { NewBlankSheetDialog } from "@/components/plan-sets/NewBlankSheetDialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Select } from "@/components/ui/select"
import { useTakeoffAutosave } from "@/hooks/use-takeoff-autosave"
import { useTakeoffLock } from "@/hooks/use-takeoff-lock"
import type { WireProductAddOn } from "@/editor-core/document"
import { toSeamSettings } from "@/editor-core/seams"
import { cn } from "@/lib/utils"
import { type SaveState, useTakeoffStore } from "@/stores/takeoff-store"
import type { AdminSettings } from "@/types/inertia"
import type {
  EditorSheet,
  LockInfo,
  SavepointRow,
  TakeoffDocument,
  TakeoffRef,
} from "@/types/takeoff"

// The editor (Konva canvas) is browser-only. It is mount-gated AND lazy so the
// SSR pass never renders it and konva never enters the SSR bundle's load path.
const Editor = lazy(() => import("@/components/editor/Editor"))

function EditorLoading() {
  return (
    <div className="flex h-full items-center justify-center">
      <p className="text-ink-muted">Loading editor…</p>
    </div>
  )
}

type Props = {
  takeoff: TakeoffRef
  sheets: EditorSheet[]
  active_sheet_id: number | null
  document: TakeoffDocument
  lock: LockInfo
  savepoints: SavepointRow[]
  settings: AdminSettings
  /** Global product-scope add-on rules — server state, not document state. */
  product_add_ons: WireProductAddOn[]
}

export default function TakeoffShowPage(props: Props) {
  const { takeoff, sheets, lock, savepoints } = props
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const { editing, claim, lockLost, lockTokenRef } = useTakeoffLock(takeoff.id)
  useTakeoffAutosave({
    takeoffId: takeoff.id,
    enabled: editing,
    lockTokenRef,
    onLockLost: lockLost,
  })

  const activeSheetId = useTakeoffStore((s) => s.activeSheetId)
  const saveState = useTakeoffStore((s) => s.saveState)
  const readOnly = useTakeoffStore((s) => s.readOnly)
  const hydrated = useTakeoffStore((s) => s.serverRevision != null)
  const serverRevision = useTakeoffStore((s) => s.serverRevision)

  // Fresh baseline whenever this page lands on a (different) takeoff. The
  // store is a module singleton, so state from a previously open takeoff
  // must never bleed into this one.
  useEffect(() => {
    const store = useTakeoffStore.getState()
    store.reset()
    store.setSettings(toSeamSettings(props.settings))
    store.setAddOnRules(props.product_add_ons)
    store.hydrate({
      rooms: props.document.rooms,
      surfaces: props.document.surfaces,
      addOns: props.document.add_ons,
      cutPlans: props.document.cut_plans,
      sheets,
      revision: props.document.revision,
      activeSheetId: props.active_sheet_id,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial hydrate per takeoff only
  }, [takeoff.id])

  // Follow server updates: viewer polling and restore redirects deliver a
  // newer document revision; the editor's own autosave echo (an older prop
  // revision than what markSaved recorded) must never re-hydrate backwards.
  useEffect(() => {
    const store = useTakeoffStore.getState()
    if (store.serverRevision != null && props.document.revision > store.serverRevision) {
      store.hydrate({
        rooms: props.document.rooms,
        surfaces: props.document.surfaces,
        addOns: props.document.add_ons,
        cutPlans: props.document.cut_plans,
        sheets,
        revision: props.document.revision,
        activeSheetId: store.activeSheetId ?? props.active_sheet_id,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revision is the trigger; the rest is read fresh
  }, [props.document.revision])

  const activeSheet = useMemo(
    () => sheets.find((s) => s.id === activeSheetId) ?? null,
    [sheets, activeSheetId]
  )

  const sheetLabels = useMemo(
    () => Object.fromEntries(sheets.map((s) => [s.id, s.label])),
    [sheets]
  )

  // Every sheet's placed crops — add-on subfloor conditions resolve per room
  // across the whole takeoff, not just the sheet on screen.
  const imagesBySheet = useMemo(
    () => Object.fromEntries(sheets.map((s) => [s.id, s.images])),
    [sheets]
  )

  const switchSheet = (id: number) => {
    useTakeoffStore.getState().setActiveSheet(id)
    // Keep the URL shareable without a server round trip — every sheet's
    // tile URL is already in props.
    window.history.replaceState(null, "", `/takeoffs/${takeoff.id}?sheet=${id}`)
  }

  const title = `${takeoff.name} — ${takeoff.owner.display_name}`
  const description = "Trace rooms and deductions on calibrated plan sheets with live square footage, linear feet, and order quantities — saved automatically."

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <EditorShell>
        <div className="flex h-full flex-col">
          <div className="flex h-10 shrink-0 items-center gap-3 border-b border-hairline bg-page px-3 text-sm">
            <Link
              href={takeoff.owner.url}
              className="shrink-0 no-underline hover:underline"
            >
              ← {takeoff.owner.display_name}
            </Link>
            <span className="shrink-0 text-ink-muted">{takeoff.name}</span>
            {sheets.length > 0 && (
              <Select
                aria-label="Sheet"
                value={activeSheetId ?? ""}
                onChange={(e) => switchSheet(Number(e.target.value))}
                className="!h-7 w-52 !py-0 text-sm"
              >
                {sheets.map((sheet) => (
                  <option key={sheet.id} value={sheet.id}>
                    {sheet.label}
                    {sheet.page_number != null
                      ? ` · p${sheet.page_number}`
                      : sheet.kind === "blank"
                        ? " · blank"
                        : " · cropped"}
                  </option>
                ))}
              </Select>
            )}
            {takeoff.owner.kind === "project" && (
              <NewBlankSheetDialog
                projectId={takeoff.owner.id}
                trigger={
                  <Button variant="ghost" size="sm" data-testid="new-blank-sheet">
                    + Blank sheet
                  </Button>
                }
              />
            )}
            {/* The revision label is the only elastic thing here — it gives
                way so the links and History stay reachable on a narrow window
                (everything else is shrink-0 and would clip instead). It gives
                way only down to a floor (see SaveStatus): fully elastic, it
                vanished outright when wide fonts crowded the row. */}
            <span className="ml-auto flex min-w-0 items-center gap-3">
              {activeSheet && (
                <span className="flex shrink-0 items-center">
                  <ScaleControls
                    blank={activeSheet.kind === "blank"}
                    images={activeSheet.images}
                  />
                </span>
              )}
              <SaveStatus readOnly={readOnly} saveState={saveState} revision={serverRevision} />
              {/* One menu for the three printable documents — three inline
                  links crowded the save status out of the bar entirely. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost" data-testid="documents-menu">
                    Documents
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link href={takeoff.schedule_url} data-testid="material-schedule-link">
                      Material schedule
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href={takeoff.worksheet_url} data-testid="worksheet-page-link">
                      Order worksheet
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href={takeoff.cut_sheet_url} data-testid="cut-sheet-page-link">
                      Cut sheet
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    {/* Per-sheet document: carry the sheet on screen along,
                        so the print opens on what the estimator is viewing. */}
                    <Link
                      href={
                        activeSheet
                          ? `${takeoff.marked_up_plan_url}?sheet=${activeSheet.id}`
                          : takeoff.marked_up_plan_url
                      }
                      data-testid="marked-up-plan-link"
                    >
                      Marked-up plan
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <span className="flex shrink-0 items-center">
                <SavepointsDialog
                  savepoints={savepoints}
                  editing={editing}
                  lockTokenRef={lockTokenRef}
                />
              </span>
            </span>
          </div>

          {readOnly && <LockBanner lock={lock} onClaim={claim} />}

          <div className="min-h-0 flex-1">
            {sheets.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <div className="callout max-w-md">
                  <p>
                    No sheets are ready to measure yet.{" "}
                    <Link href={takeoff.owner.url}>
                      Promote a page from a plan
                    </Link>{" "}
                    to start.
                  </p>
                </div>
              </div>
            ) : mounted && hydrated && activeSheet ? (
              <Suspense fallback={<EditorLoading />}>
                <Editor
                  key={activeSheet.id}
                  images={activeSheet.images}
                  mirrored={activeSheet.mirrored}
                  sheetKind={activeSheet.kind}
                  sheetSize={
                    activeSheet.width != null && activeSheet.height != null
                      ? { width: activeSheet.width, height: activeSheet.height }
                      : null
                  }
                  sheetLabels={sheetLabels}
                  sheetId={activeSheet.id}
                  imagesBySheet={imagesBySheet}
                  projectSubfloor={takeoff.subfloor_default}
                  wasteModeDefault={takeoff.waste_mode_default}
                  cutSheetUrl={takeoff.cut_sheet_url}
                  worksheetUrl={takeoff.worksheet_url}
                />
              </Suspense>
            ) : (
              <EditorLoading />
            )}
          </div>
        </div>
      </EditorShell>
    </>
  )
}

const SAVE_LABELS: Record<SaveState, string> = {
  idle: "",
  dirty: "Unsaved changes",
  saving: "Saving…",
  saved: "Saved",
  error: "Save failed — retrying",
}

function SaveStatus({
  readOnly,
  saveState,
  revision,
}: {
  readOnly: boolean
  saveState: SaveState
  revision: number | null
}) {
  const label = readOnly
    ? "Read-only"
    : saveState === "saved" && revision != null
      ? `Saved · rev ${revision}`
      : SAVE_LABELS[saveState]
  if (!label) return null
  return (
    <span
      data-testid="save-status"
      title={label}
      className={cn(
        // min-w floor, not min-w-0: this span is the toolbar's elastic
        // element, and with zero floor a crowded row crushes it to 0px —
        // the save indicator silently disappears (WebDriver then reads its
        // text as "", which is how CI runners with wide Linux fallback
        // fonts failed 6 system tests that pass on macOS). The floor keeps
        // it legible; truncate still lets it give way above the floor.
        "min-w-20 truncate text-xs",
        saveState === "error" && !readOnly ? "text-danger" : "text-ink-muted"
      )}
    >
      {label}
    </span>
  )
}

function LockBanner({ lock, onClaim }: { lock: LockInfo; onClaim: () => void }) {
  const claimable = !lock.holder || lock.stale || lock.mine
  return (
    <div className="flex min-h-9 shrink-0 flex-wrap items-center gap-3 border-b border-hairline bg-accent-faded px-3 py-1 text-sm">
      <span>
        {lock.holder && lock.mine
          ? "You're editing this takeoff in another window."
          : lock.holder
            ? `${lock.holder.name} is editing — you're viewing read-only.`
            : "This takeoff is available."}
      </span>
      {claimable && (
        <Button size="sm" variant="secondary" onClick={onClaim}>
          {lock.holder && !lock.mine ? "Take over" : lock.mine ? "Edit here" : "Start editing"}
        </Button>
      )}
    </div>
  )
}

function SavepointsDialog({
  savepoints,
  editing,
  lockTokenRef,
}: {
  savepoints: SavepointRow[]
  editing: boolean
  lockTokenRef: RefObject<string | null>
}) {
  const [confirmingId, setConfirmingId] = useState<number | null>(null)

  const restore = (savepoint: SavepointRow) => {
    router.post(savepoint.restore_url, { lock_token: lockTokenRef.current ?? "" })
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost">History</Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Save history</DialogTitle>
          <DialogDescription>
            {editing
              ? "Automatic savepoints — restore rolls the rooms back to that moment (a safety savepoint of the current state is kept)."
              : "Automatic savepoints. Only the active editor can restore."}
          </DialogDescription>
        </DialogHeader>
        {savepoints.length === 0 ? (
          <p className="text-ink-muted">No savepoints yet — they appear as you measure.</p>
        ) : (
          <ul className="max-h-80 divide-y divide-hairline overflow-y-auto">
            {savepoints.map((savepoint) => (
              <li key={savepoint.id} className="flex items-center gap-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="block truncate">
                    {formatDateTime(savepoint.created_at)} · {savepoint.created_by}
                  </span>
                  <span className="text-xs text-ink-muted">
                    {savepoint.room_count} {savepoint.room_count === 1 ? "room" : "rooms"}
                    {savepoint.surface_count > 0 &&
                      ` · ${savepoint.surface_count} ${savepoint.surface_count === 1 ? "surface" : "surfaces"}`}
                    {savepoint.add_on_count > 0 &&
                      ` · ${savepoint.add_on_count} ${savepoint.add_on_count === 1 ? "add-on" : "add-ons"}`}
                  </span>
                </span>
                {editing && (
                  <span className="ml-auto shrink-0">
                    {confirmingId === savepoint.id ? (
                      <span className="flex items-center gap-1">
                        <Button size="sm" variant="danger" onClick={() => restore(savepoint)}>
                          Confirm restore
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmingId(null)}>
                          Cancel
                        </Button>
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setConfirmingId(savepoint.id)}
                      >
                        Restore
                      </Button>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        <DialogClose asChild>
          <Button variant="ghost" className="mt-2 self-end">Close</Button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
